import { styled } from 'styled-components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ButtonProps, Input, InputRef, Modal, ModalProps, Typography } from 'antd';
import { computeRemSize } from '@ydistri/theme';

const { Text } = Typography;

export interface GenericInputModalProps extends Omit<
  ModalProps,
  | 'open'
  | 'title'
  | 'onOk'
  | 'onCancel'
  | 'okText'
  | 'okButtonProps'
  | 'cancelButtonProps'
  | 'width'
> {
  title: string;
  placeholder: string;
  disabled?: boolean;
  submitDisabled?: boolean;
  onConfirmed: (value: string) => void;
  onClose: () => void;
  submitButtonLabel: string;
  requiredMessage: string;
  error?: boolean;
  width?: string | number;
  defaultValue?: string;
}

const InputWrapper = styled.div`
  padding: ${computeRemSize(24)} 0;
`;

export const GenericInputModal: React.FC<GenericInputModalProps> = ({
  title,
  placeholder,
  disabled = false,
  submitDisabled = false,
  onConfirmed,
  onClose,
  submitButtonLabel,
  requiredMessage,
  error,
  width = 640,
  defaultValue,
  ...rest
}) => {
  const [inputValue, setInputValue] = React.useState<string | undefined>(defaultValue);
  const [isEmptyInput, setIsEmptyInput] = useState(false);
  const inputRef = useRef<InputRef>(null);

  const okButtonProps: ButtonProps = useMemo(
    () => ({
      disabled: disabled || submitDisabled,
    }),
    [disabled, submitDisabled],
  );

  const cancelButtonProps: ButtonProps = useMemo(
    () => ({
      disabled,
    }),
    [disabled],
  );

  const closeHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClose();
    },
    [onClose],
  );

  const onOk = useCallback(() => {
    if (!inputValue || inputValue.length === 0) {
      setIsEmptyInput(true);
    } else {
      onConfirmed(inputValue);
    }
  }, [inputValue, onConfirmed]);

  const okHandler = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onOk();
    },
    [onOk],
  );

  const inputChangeHandler = useCallback((event: { target: { value: string | undefined } }) => {
    setIsEmptyInput(false);
    setInputValue(event.target.value);
  }, []);

  /**
   * If the Enter key was pressed, we treat it is as a Confirm button click
   */
  const onKeyUp = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onOk();
      }
    },
    [onOk],
  );

  /**
   * Autofocus the input element when mounted for better user experience
   * as there is only one input element where the user needs to write something.
   */
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  return (
    <Modal
      data-type="modal"
      open={true}
      title={title}
      okButtonProps={okButtonProps}
      cancelButtonProps={cancelButtonProps}
      closable={false}
      onCancel={closeHandler}
      onOk={okHandler}
      okText={submitButtonLabel}
      width={width}
      {...rest}
    >
      <InputWrapper>
        <Input
          size="large"
          value={inputValue}
          onChange={inputChangeHandler}
          placeholder={placeholder}
          data-testid="GenericInputModalInput"
          disabled={disabled}
          status={isEmptyInput || error ? 'error' : undefined}
          onKeyUp={onKeyUp}
          ref={inputRef}
        />
        {isEmptyInput && <Text type="danger">{requiredMessage}</Text>}
      </InputWrapper>
    </Modal>
  );
};
